import { Box, VStack, Grid, Separator, Text, HStack, Button, Badge } from "@chakra-ui/react";
import SectionHeader from "../../CustomUi/SectionHeader";
import { BiPlus } from "react-icons/bi";
import { MdAccountBalance } from "react-icons/md";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import { useEffect, useState } from "react";
import Loader from "../../CustomUi/Loader";
import CustomAlert from "../../CustomUi/CustomAlert";
import Empty from "../../CustomUi/Empty";
import Cad from "../../CustomUi/Cad";
import { useDisclosure } from "@chakra-ui/react";
import { virtualAccountService, VirtualAccountDto, GuardianWithoutBvnDto } from "../../../services/virtualAccountService";
import useAxios from "../../../hooks/useAxios";

function VirtualAccountsPage() {
    const { school, accentColor } = useSchoolsContext();
    const axiosClient = useAxios();
    const [accounts, setAccounts] = useState<VirtualAccountDto[]>([]);
    const [guardiansWithoutVa, setGuardiansWithoutVa] = useState<GuardianWithoutBvnDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [bulkCreating, setBulkCreating] = useState(false);
    const { onOpen } = useDisclosure(); // TODO: Use onOpen when implementing create modal

    useEffect(() => {
        if (school?.id) {
            loadData();
        }
    }, [school]);

    const loadData = async () => {
        if (!school?.id) return;

        try {
            setIsLoading(true);
            const [accountsResponse, guardiansResponse] = await Promise.all([
                virtualAccountService.getSchoolVirtualAccounts(axiosClient, school.id),
                virtualAccountService.getGuardiansWithoutVa(axiosClient, school.id),
            ]);

            if (accountsResponse.succeeded) {
                setAccounts(accountsResponse.data || []);
            }
            if (guardiansResponse.succeeded) {
                setGuardiansWithoutVa(guardiansResponse.data || []);
            }
        } catch (err) {
            setError("Failed to load virtual accounts");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkCreate = async () => {
        if (!school?.id) return;

        try {
            setBulkCreating(true);
            const response = await virtualAccountService.bulkCreateVirtualAccounts(axiosClient, school.id);
            if (response.succeeded) {
                loadData();
            }
        } catch (err) {
            console.error("Failed to bulk create VAs", err);
        } finally {
            setBulkCreating(false);
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <CustomAlert message={error} handleClick={loadData} />;
    }

    return (
        <VStack gap={8}>
            {/* Guardians Without VA Alert */}
            {guardiansWithoutVa.length > 0 && (
                <Cad>
                    <VStack align="stretch" gap={4}>
                        <HStack justify="space-between">
                            <VStack align="start" gap={1}>
                                <Text fontWeight="bold">Guardians Without Virtual Accounts</Text>
                                <Text fontSize="sm" color="gray.600">
                                    {guardiansWithoutVa.length} guardians need virtual accounts created
                                </Text>
                            </VStack>
                            <Button
                                onClick={handleBulkCreate}
                                colorScheme="teal"
                                loading={bulkCreating}
                            >
                                Bulk Create for Guardians with BVN
                            </Button>
                        </HStack>
                    </VStack>
                </Cad>
            )}

            {/* Virtual Accounts List */}
            <Cad>
                <VStack gap={6} align="stretch">
                    <SectionHeader
                        title="Virtual Accounts"
                        subtitle="Manage parent virtual accounts for payments"
                        icon={MdAccountBalance}
                        buttonText="Create VA"
                        onButtonClick={onOpen}
                        buttonIcon={BiPlus}
                        buttonColorScheme={accentColor}
                    />

                    <Separator />

                    {accounts.length > 0 ? (
                        <Grid
                            templateColumns={{
                                base: "1fr",
                                md: "repeat(2, 1fr)",
                                xl: "repeat(3, 1fr)",
                            }}
                            gap={6}
                        >
                            {accounts.map((account) => (
                                <Cad key={account.id}>
                                    <VStack align="stretch" gap={3}>
                                        <HStack justify="space-between">
                                            <Text fontWeight="bold" fontSize="lg">
                                                {account.guardianName}
                                            </Text>
                                            {account.isActive && (
                                                <Badge colorScheme="green">Active</Badge>
                                            )}
                                        </HStack>
                                        <VStack align="stretch" gap={1}>
                                            <Text fontSize="sm" color="gray.600">
                                                Account: {account.accountNumber}
                                            </Text>
                                            <Text fontSize="sm" color="gray.600">
                                                Bank: {account.bankName}
                                            </Text>
                                            <Text fontSize="sm" color="gray.600">
                                                Name: {account.accountName}
                                            </Text>
                                        </VStack>
                                        {account.lastPaymentAt && (
                                            <Text fontSize="xs" color="gray.500">
                                                Last payment: {new Date(account.lastPaymentAt).toLocaleDateString()}
                                            </Text>
                                        )}
                                    </VStack>
                                </Cad>
                            ))}
                        </Grid>
                    ) : (
                        <Box py={8}>
                            <Empty
                                message="No virtual accounts created yet"
                                hasButton
                                buttonText="Create First VA"
                                handleClick={onOpen}
                            />
                        </Box>
                    )}
                </VStack>
            </Cad>
        </VStack>
    );
}

export default VirtualAccountsPage;
